import React, { useEffect, useState } from "react";
import { Close, InfoOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import DiscardChangesModal from "pages/homeDashboard/components/Modals/DiscardChangesModal/discardChangesModal";
import { useLazyGetAdCheckEmailQuery } from "state/rtkQuery/user/userQueries";

import LoadingButton from "components/LoadingButton/LoadingButton";

const EmailTag = ({ email, onRemove }) => {
    return (
        <div style={{
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: "#f1f1f1",
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: "4px 8px",
            margin: "4px",
            fontSize: "14px"
        }}>
            {email}
            <Close
                style={{
                    fontSize: "16px",
                    marginLeft: "6px",
                    cursor: "pointer",
                    color: "#666"
                }}
                onClick={() => onRemove(email)}
            />
        </div>
    );
};

const validateGithubOrgUrl = url =>
    /^https:\/\/github\.com\/[A-Za-z0-9_.-]+$/.test(url);

const validateUrl = url =>
    /^https:\/\/[^\s/$.?#].[^\s]*$/.test(url);

const validateEmail = email =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);


const EditOrgModal = ({ org, isOpen, onClose, onSave, isLoading }) => {
    const [addEmailError, setAddEmailError] = useState("");
    const [triggerAdCheckEmail, { isLoading: isAddLoading }] = useLazyGetAdCheckEmailQuery();
    const [isDirty, setIsDirty] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        approvers: [],
        url: "",
        service_owner: "",
        copilot_link: "",
        org_website: "",
        countries: ""
    });

    const [newEmail, setNewEmail] = useState("");
    const [errors, setErrors] = useState({});

    // Reset form when org changes
    useEffect(() => {
        if (org) {
            const info = org.additional_info || {};
            let countriesDisplay = "";
            if (org.countries) {
                if (typeof org.countries === 'string') {
                    countriesDisplay = org.countries;
                } else if (Array.isArray(org.countries)) {
                    countriesDisplay = org.countries.join(", ");
                }
            }
            setFormData({
                name: org.name || "",
                approvers: org.approvers || [],
                url: info.github_org_link || "",
                service_owner: org.service_owner || "",
                copilot_link: info.custom_copilot_page || "",
                org_website: info.custom_org_website || "",
                countries: countriesDisplay
            });
        }
    }, [org]);

    useEffect(() => {
        if (org) {
            const info = org.additional_info || {};
            let countriesDisplay = "";
            if (org.countries) {
                if (typeof org.countries === 'string') {
                    countriesDisplay = org.countries;
                } else if (Array.isArray(org.countries)) {
                    countriesDisplay = org.countries.join(", ");
                }
            }
            const initialData = {
                name: org.name || "",
                approvers: org.approvers || [],
                url: info.github_org_link || "",
                service_owner: org.service_owner || "",
                copilot_link: info.custom_copilot_page || "",
                org_website: info.custom_org_website || "",
                countries: countriesDisplay
            };
            setFormData(initialData);
            setIsDirty(false); // Reset dirty state on open/change
        }
    }, [org]);

    useEffect(() => {
        const newErrors = {};
        if (formData.url && !validateGithubOrgUrl(formData.url)) {
            newErrors.url = "Must be a valid GitHub organization URL (e.g. https://github.com/orgname)";
        }
        if (formData.copilot_link && !validateUrl(formData.copilot_link)) {
            newErrors.copilot_link = "Must be a valid URL";
        }
        if (formData.org_website && !validateUrl(formData.org_website)) {
            newErrors.org_website = "Must be a valid URL";
        }
        if (formData.service_owner && !validateEmail(formData.service_owner)) {
            newErrors.service_owner = "Must be a valid email address";
        } setErrors(newErrors);
    }, [formData]);

    // Clear errors when modal is opened
    useEffect(() => {
        if (isOpen) {
            setAddEmailError("");
            setErrors({});
        }
    }, [isOpen]);


    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setIsDirty(true);
    };

    const handleAddEmail = async () => {
        const trimmedEmail = newEmail.trim().toLowerCase();

        // 1. Validate email format
        if (!trimmedEmail || !validateEmail(trimmedEmail)) {
            setAddEmailError("Enter a valid email address.");
            return;
        }

        // 2. Check for duplicate in approvers
        if (formData.approvers.map(e => e.toLowerCase()).includes(trimmedEmail)) {
            setAddEmailError("This email is already an approver.");
            return;
        }
        // 3. Validate with AD API
        try {
            const data = await triggerAdCheckEmail(trimmedEmail).unwrap();
            if (!data?.exists_in_ad) {
                setAddEmailError("User is not valid. Please try again.");
                showToast({
                    title: `Invalid User`,
                    description: `The email ${trimmedEmail} is not valid. Please try again.`,
                    type: "error",
                    duration: 5000,
                });
                setNewEmail("");
                return;
            }
        } catch (e) {
            setAddEmailError("User is not valid. Please try again.");
            setNewEmail("");
            return;
        }
        // Success: add to approvers
        setFormData({
            ...formData,
            approvers: [...formData.approvers, trimmedEmail]
        });
        setNewEmail("");
        setAddEmailError("");
        setIsDirty(true);
    };

    const handleRemoveEmail = (emailToRemove) => {
        setFormData({
            ...formData,
            approvers: formData.approvers.filter(email => email !== emailToRemove)
        });
        setIsDirty(true);
    };

    // const validateFields = () => {
    //     const newErrors = {};
    //     if (formData.url && !validateGithubOrgUrl(formData.url)) {
    //         newErrors.url = "Must be a valid GitHub organization URL (e.g. https://github.com/orgname)";
    //     }
    //     if (formData.copilot_link && !validateUrl(formData.copilot_link)) {
    //         newErrors.copilot_link = "Must be a valid URL (http/https)";
    //     }
    //     if (formData.org_website && !validateUrl(formData.org_website)) {
    //         newErrors.org_website = "Must be a valid URL (http/https)";
    //     }
    //     setErrors(newErrors);
    //     return Object.keys(newErrors).length === 0;
    // };

    const handleSave = () => {
        // if (!validateFields()) return;
        if (Object.keys(errors).length > 0) return;
        onSave(org._id, formData);
        setIsDirty(false);
    };

    const handleNewEmailChange = (e) => {
        setNewEmail(e.target.value);
        setAddEmailError(""); // Clear error on change
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddEmail();
        }
    };

    const handleClose = () => {
        if (isDirty) {
            setShowDiscardModal(true);
            return;
        }
        onClose();
    };

    const handleDiscard = () => {
        setShowDiscardModal(false);
        onClose();
    };
    if (!isOpen) return null;

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                width: "600px",
                maxWidth: "90%",
                maxHeight: "90vh",
                overflowY: "auto"
            }}>
                <label style={{ fontSize: "20px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>Edit Organization: {org?.name}</label>

                <div style={{ marginTop: "10px", marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px" }}>Organization Name</label>
                    <input
                        type="text"
                        name="name"
                        disabled={true}
                        value={formData.name}
                        onChange={handleChange}
                        style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            backgroundColor: "#f5f5f5",
                            cursor: "not-allowed"
                        }}
                    />
                </div>

                <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px" }}>Countries</label>
                    <input
                        type="text"
                        name="countries"
                        disabled={true}
                        value={formData.countries || "N/A"}
                        style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            backgroundColor: "#f5f5f5",
                            cursor: "not-allowed"
                        }}
                    />
                </div>

                <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "flex", alignItems: "center", marginBottom: "5px", gap: "6px" }}>
                        Approvers
                        <Tooltip
                            title={
                                <div style={{ fontSize: "13px", maxWidth: 220 }}>
                                    <div>Press <b>X</b> to remove an approver.</div>
                                    <div>Enter an email in the input box and click <b>Add</b> to add an approver.</div>
                                    <div style={{ marginTop: 6, color: "#f1f1f1ff" }}>
                                        <b>Note:</b> Changes will only be saved if you click <b>Save Changes</b>.
                                    </div>
                                </div>
                            }
                            arrow
                            placement="right"
                        >
                            <InfoOutlined style={{ fontSize: 18, color: "#1976d2", cursor: "pointer" }} />
                        </Tooltip>
                    </label>
                    <div style={{
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "8px",
                        minHeight: "40px",
                        marginBottom: "8px",
                        maxHeight: "120px",
                        overflowY: "auto"
                    }}>
                        {formData.approvers.map(email => (
                            <EmailTag
                                key={email}
                                email={email}
                                onRemove={handleRemoveEmail}
                            />
                        ))}
                    </div>
                    <div style={{ display: "flex" }}>
                        <input
                            type="text"
                            placeholder="Add an approver (Enter email address)"
                            value={newEmail}
                            onChange={handleNewEmailChange}
                            onKeyDown={handleKeyDown}
                            style={{
                                flex: 1,
                                padding: "8px",
                                border: "1px solid #ccc",
                                borderRadius: "4px 0 0 4px"
                            }}
                            disabled={isAddLoading}
                        />
                        <LoadingButton
                            onClickFunc={handleAddEmail}
                            loadingState={isAddLoading}
                            disabled={isAddLoading || !!addEmailError || !newEmail.trim()}
                            variant="contained"
                            backgroundColor={isAddLoading ? "#ccc" : "#0b756c"}
                            color="white"
                            text="Add"
                            loadingText="Validating..."
                            style={{
                                borderRadius: "0 4px 4px 0",
                                padding: "8px 16px",
                                minWidth: "140px", // <-- Increase minWidth for loading text
                                fontSize: "15px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        />
                    </div>
                    {addEmailError && <div style={{ color: "#d32f2f", fontSize: "13px", marginTop: "3px" }}>{addEmailError}</div>}
                </div>

                <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px" }}>Org URL</label>
                    <input
                        type="text"
                        name="url"
                        value={formData.url}
                        onChange={handleChange}
                        style={{
                            width: "100%",
                            padding: "8px",
                            border: errors.url ? "1px solid #d32f2f" : "1px solid #ccc",
                            borderRadius: "4px"
                        }}
                    />
                    {errors.url && <div style={{ color: "#d32f2f", fontSize: "13px", marginTop: "3px" }}>{errors.url}</div>}
                </div>

                <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px" }}>Service Owner</label>
                    <input
                        type="text"
                        name="service_owner"
                        value={formData.service_owner}
                        onChange={handleChange}
                        style={{
                            width: "100%",
                            padding: "8px",
                            border: errors.service_owner ? "1px solid #d32f2f" : "1px solid #ccc",
                            borderRadius: "4px"
                        }}
                    />
                    {errors.service_owner && <div style={{ color: "#d32f2f", fontSize: "13px", marginTop: "3px" }}>{errors.service_owner}</div>}
                </div>

                <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px" }}>Copilot Requests Info</label>
                    <input
                        type="text"
                        name="copilot_link"
                        value={formData.copilot_link}
                        onChange={handleChange}
                        style={{
                            width: "100%",
                            padding: "8px",
                            border: errors.copilot_link ? "1px solid #d32f2f" : "1px solid #ccc",
                            borderRadius: "4px"
                        }}
                        placeholder="Enter Copilot link URL"
                    />
                    {errors.copilot_link && <div style={{ color: "#d32f2f", fontSize: "13px", marginTop: "3px" }}>{errors.copilot_link}</div>}
                </div>

                <div style={{ marginBottom: "15px" }}>
                    <label style={{ display: "block", marginBottom: "5px" }}>Organization Website</label>
                    <input
                        type="text"
                        name="org_website"
                        value={formData.org_website}
                        onChange={handleChange}
                        style={{
                            width: "100%",
                            padding: "8px",
                            border: errors.org_website ? "1px solid #d32f2f" : "1px solid #ccc",
                            borderRadius: "4px"
                        }}
                        placeholder="Enter website URL"
                    />
                    {errors.org_website && <div style={{ color: "#d32f2f", fontSize: "13px", marginTop: "3px" }}>{errors.org_website}</div>}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                    <button
                        onClick={handleClose}
                        disabled={isLoading}
                        style={{
                            padding: "8px 16px",
                            background: "#f5f5f5",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            cursor: isLoading ? "not-allowed" : "pointer"
                        }}
                    >
                        Cancel
                    </button>
                    <LoadingButton
                        onClickFunc={handleSave}
                        loadingState={isLoading}
                        disabled={isLoading || Object.keys(errors).length > 0}
                        variant="contained"
                        backgroundColor="#0b756c"
                        color="white"
                        text="Save Changes"
                        loadingText="Updating Org Details..."
                        style={{ cursor: isLoading || Object.keys(errors).length > 0 ? "not-allowed" : "pointer" }}
                    />
                </div>
            </div>
            <DiscardChangesModal
                open={showDiscardModal}
                onClose={() => setShowDiscardModal(false)}
                onDiscard={handleDiscard}
            />
        </div>
    );
};

export default EditOrgModal;